import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { restockProductUrl, restockWarehouseProductUrl } from "../../../api/endpoints";
import { getAllData, saveData } from "../../../tools/indexdb";
import { ButtonLoader } from "../../../components/uxCollections/Loaders";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";

const RestockOverlay = ({ refreshData = false, product, closeModal, warehouse = true, updateSelectedProduct = false }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [formData, setFormData] = useState({
    quantity: "",
    costPrice: "",
    unitPrice: "",
  });
  const [isLoading, setIsLoading] = useState(false); //
  const [isFormValid, setIsFormValid] = useState(false);
  const { user } = useSelector((state) => state.webStoreUser);
  const { warehouse: warehouseData } = useSelector((state) => state.warehouse);
  const { store } = useSelector((state) => state.store);
  const { employee } = useSelector((state) => state.employee);
  useEffect(() => {
    const { quantity, costPrice, unitPrice } = formData;
    if (quantity && costPrice && unitPrice) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [formData]);

  //url endpoints

  let endpoint = warehouse ? restockWarehouseProductUrl + warehouseData._id : restockProductUrl + store._id;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: Number(value),
    }));
  };

  const handleRestock = async () => {
    if (employee && !employee.allowedActions.canEditProducts) {
      uiAlert("You do not have access to perform this operation. contact admin", "error");
      return;
    }

    let { quantity, costPrice: newCostPrice, unitPrice: newUnitPrice } = formData;

    let accessToken = localStorage.getItem("accessToken");
    let restockPayload = {
      productID: warehouse ? product._id : product._id,
      quantity,
      newCostPrice,
      newUnitPrice,
      employee,
    };
    if (newCostPrice >= newUnitPrice && !window.confirm("Unit price is less than or equal to cost price. Are you sure you want to proceed?")) {
      return; // user clicked Cancel -> stop here
    }

    if (isFormValid) {
      setIsLoading(true);
      try {
        let response = await fetch(endpoint, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(restockPayload),
        });
        if (response.ok) {
          let { storeProductCollection, product } = await response.json();
          console.log(product, "product after restock");

          if (warehouse) {
            console.log("warhouse");
            /*      refreshData((prev) => !prev); */
            refreshData();
            closeModal(false);
            uiAlert("Product restocked successfully in the warehouse", "success");
          } else {
            let storeData = await getAllData(store.storeName);

            if (storeData) {
              storeData.storeProductCollection = storeProductCollection.products;

              await saveData(store.storeName, storeData);

              uiAlert("Product restocked successfully", "success");

              refreshData ? refreshData((prev) => !prev) : updateSelectedProduct(product);
              closeModal(false);
            }
          }
          return;
        }
        throw new Error("Failed to restock product");
      } catch (error) {
        console.log(error);
        return uiAlert("Error restocking product. Please try again later.", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setIsVisible(false);
    closeModal(false);
  };

  if (!isVisible) return null;

  const allowDigits = (e) => {
    if (e.inputType?.startsWith("insert") && e.data && /[^0-9]/.test(e.data)) {
      e.preventDefault();
    }
  };
  const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent);

  return (
    <div className='phantom-overlay-backdrop'>
      <div className='quantum-restock-modal'>
        {/* Header Section */}
        <div className='cosmic-header-zone'>
          <h2 className='stellar-title-text'>Restock Product</h2>

          <div className='orbital-product-info'>
            <div className='mercury-product-label'>Product:</div>
            <div className='venus-product-details'>
              <div className='mars-product-name'>
                {product.name}
                <span className='jupiter-stock-indicator' style={{ fontSize: "0.7rem", color: `${product.quantity > 10 ? "green" : "red"}` }}>
                  ({product.quantity} in stock)
                  <b>{warehouse && product.warehouseQuantity && `(${product.warehouseQuantity} in warehouse)`}</b>
                </span>
              </div>
              <div className='saturn-product-id'>Product ID: {product._id}</div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className='galaxy-form-container'>
          {/* Quantity Input */}
          <div className='asteroid-input-cluster'>
            <label className='comet-input-label'>Quantity </label>
            <div className='pulsar-input-wrapper'>
              <input
                pattern={isMobileDevice ? "[0-9]*" : undefined}
                inputMode='numeric'
                type={isMobileDevice ? "text" : "number"}
                onBeforeInput={isMobileDevice ? allowDigits : undefined}
                className='meteor-input-field'
                value={formData.quantity}
                onChange={(e) => handleInputChange("quantity", e.target.value)}
                placeholder='Quantity to restock'
              />
              <span className='eclipse-hamburger-icon'>☰</span>
            </div>
          </div>

          {/* New Cost Price Input */}
          <div className='asteroid-input-cluster'>
            <label className='comet-input-label'>New cost price</label>
            <div className='supernova-price-container'>
              <input
                pattern={isMobileDevice ? "[0-9]*" : undefined}
                inputMode='numeric'
                type={isMobileDevice ? "text" : "number"}
                onBeforeInput={isMobileDevice ? allowDigits : undefined}
                className='meteor-input-field quasar-price-input'
                value={formData.costPrice}
                onChange={(e) => handleInputChange("costPrice", e.target.value)}
                placeholder={`${user.userCountryCurrencySymbol} ${product.costPrice.toLocaleString()} now`}
              />
              <span className='blackhole-currency-label'> {user.userCountryCurrencySymbol}</span>
            </div>
          </div>

          {/* New Unit Price Input */}
          <div className='asteroid-input-cluster'>
            <label className='comet-input-label'>New unit price</label>
            <div className='supernova-price-container'>
              <input
                type='number'
                className='meteor-input-field quasar-price-input'
                value={formData.unitPrice}
                onChange={(e) => handleInputChange("unitPrice", e.target.value)}
                placeholder={`${user.userCountryCurrencySymbol} ${product.unitPrice.toLocaleString()} now`}
              />
              <span className='blackhole-currency-label'>{user.userCountryCurrencySymbol}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='vortex-button-galaxy'>
            {isFormValid ? (
              <button disabled={isLoading} className='wormhole-primary-action' onClick={handleRestock}>
                {isLoading ? <ButtonLoader /> : "Restock"}
              </button>
            ) : (
              <button className='wormhole-primary-action' style={{ opacity: "0.5", cursor: "not-allowed" }} disabled>
                Restock
              </button>
            )}
            <button className='singularity-cancel-action' onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .phantom-overlay-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          display: flex;

          justify-content: center;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .quantum-restock-modal {
          margin: auto;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          width: 90%;
          max-width: 420px;
          padding: 0;
          animation: nebula-modal-appear 0.3s ease-out;
          height: fit-content;
        }

        @keyframes nebula-modal-appear {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .cosmic-header-zone {
          padding: 24px 24px 20px;
          border-bottom: 1px solid #e8e8e8;
          text-align: center;
        }

        .stellar-title-text {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 16px;
        }

        .orbital-product-info {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          text-align: left;
        }

        .mercury-product-label {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .venus-product-details {
          text-align: right;
        }

        .mars-product-name {
          font-size: 14px;
          color: #1a1a1a;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .jupiter-stock-indicator {
          font-size: 12px;
          color: #10b981;
          background-color: #ecfdf5;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 8px;
        }

        .saturn-product-id {
          font-size: 11px;
          color: #9ca3af;
          margin-top: 4px;
        }

        .galaxy-form-container {
          padding: 24px;
        }

        .asteroid-input-cluster {
          margin-bottom: 20px;
        }

        .comet-input-label {
          display: block;
          font-size: 14px;
          color: #374151;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .pulsar-input-wrapper {
          position: relative;
        }

        .meteor-input-field {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          color: #1f2937;
          transition: all 0.2s ease;
          background-color: #f9fafb;
        }

        .meteor-input-field:focus {
          outline: none;
          border-color: #3b82f6;
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .eclipse-hamburger-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 16px;
          color: #6b7280;
          cursor: pointer;
        }

        .supernova-price-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .quasar-price-input {
          padding-right: 40px !important;
        }

        .blackhole-currency-label {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
          pointer-events: none;
        }

        .vortex-button-galaxy {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 24px;
        }

        @media (max-width: 480px) {
          .vortex-button-galaxy {
            gap: 10px;
            margin-top: 20px;
          }
        }

        @media (max-width: 360px) {
          .vortex-button-galaxy {
            gap: 8px;
            margin-top: 16px;
          }
        }

        .wormhole-primary-action {
          width: 100%;
          padding: 14px 24px;
          background-color: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          min-height: 48px;
          touch-action: manipulation;
          box-sizing: border-box;
        }

        @media (max-width: 480px) {
          .wormhole-primary-action {
            padding: 12px 20px;
            font-size: 16px; /* Better for mobile tapping */
            border-radius: 6px;
            min-height: 46px;
          }
        }

        @media (max-width: 360px) {
          .wormhole-primary-action {
            padding: 11px 18px;
            min-height: 44px;
          }
        }

        .wormhole-primary-action:hover {
          background-color: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        @media (hover: none) {
          .wormhole-primary-action:hover {
            transform: none;
            box-shadow: none;
          }
        }

        .singularity-cancel-action {
          width: 100%;
          padding: 12px 24px;
          background-color: transparent;
          color: #ef4444;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-height: 44px;
          touch-action: manipulation;
          box-sizing: border-box;
        }

        @media (max-width: 480px) {
          .singularity-cancel-action {
            padding: 10px 20px;
            font-size: 16px; /* Better for mobile tapping */
            border-radius: 6px;
            min-height: 42px;
          }
        }

        @media (max-width: 360px) {
          .singularity-cancel-action {
            padding: 9px 18px;
            min-height: 40px;
          }
        }

        .singularity-cancel-action:hover {
          background-color: #fef2f2;
          color: #dc2626;
        }

        @media (hover: none) {
          .singularity-cancel-action:hover {
            background-color: transparent;
          }
        }

        .meteor-input-field::placeholder {
          color: #9ca3af;
          font-size: 13px;
        }

        @media (max-width: 480px) {
          .meteor-input-field::placeholder {
            font-size: 14px;
          }
        }

        /* Landscape mobile optimization */
        @media screen and (max-height: 500px) and (orientation: landscape) {
          .phantom-overlay-backdrop {
            padding: 8px;
          }

          .quantum-restock-modal {
            max-height: 95vh;
          }

          .cosmic-header-zone {
            padding: 12px 16px 10px;
          }

          .stellar-title-text {
            font-size: 16px;
            margin-bottom: 8px;
          }

          .galaxy-form-container {
            padding: 12px 16px;
          }

          .asteroid-input-cluster {
            margin-bottom: 12px;
          }

          .vortex-button-galaxy {
            margin-top: 16px;
            gap: 8px;
          }
        }

        /* High DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .meteor-input-field {
            border-width: 1px;
          }
        }

        /* Accessibility improvements */
        @media (prefers-reduced-motion: reduce) {
          .quantum-restock-modal {
            animation: none;
          }

          .meteor-input-field,
          .wormhole-primary-action,
          .singularity-cancel-action {
            transition: none;
          }
        }

        /* Focus visibility */
        .wormhole-primary-action:focus-visible,
        .singularity-cancel-action:focus-visible,
        .meteor-input-field:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
};

export default RestockOverlay;
